import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { FileDown, Plus, Trash2, Send, PackageCheck, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PO_STATUS = {
  draft: { tkey: 'admin.supply_page.status_draft', fallback: 'Koncept', cls: 'bg-slate-200 text-slate-800' },
  sent: { tkey: 'admin.supply_page.status_sent', fallback: 'Odoslané dodávateľovi', cls: 'bg-blue-200 text-blue-900' },
  received: { tkey: 'admin.supply_page.status_received', fallback: 'Prijaté na sklad', cls: 'bg-emerald-200 text-emerald-900' },
  cancelled: { tkey: 'admin.supply_page.status_cancelled', fallback: 'Zrušené', cls: 'bg-red-200 text-red-900' },
};

const TABS_DEF = [
  { key: 'backorders', tkey: 'admin.supply_page.tab_backorders', fallback: 'Backorders' },
  { key: 'suppliers', tkey: 'admin.supply_page.tab_suppliers', fallback: 'Dodávatelia' },
  { key: 'pos', tkey: 'admin.supply_page.tab_pos', fallback: 'Objednávky tovaru' },
];

// ===== Utilities =====
const formatDate = (iso, lang = 'sk') => {
  if (!iso) return '';
  const map = { sk: 'sk-SK', cs: 'cs-CZ', en: 'en-US', de: 'de-DE', ru: 'ru-RU' };
  return new Date(iso).toLocaleString(map[lang] || 'sk-SK', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ===== Main page =====
export default function AdminSupply() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [tab, setTab] = useState('backorders');
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6" data-testid="admin-supply-title">{t('admin.supply_page.title', 'Zásoby a objednávky')}</h1>

      <div className="bg-white rounded-xl shadow p-2 mb-6 inline-flex gap-2">
        {TABS_DEF.map(td => (
          <button
            key={td.key}
            onClick={() => setTab(td.key)}
            data-testid={`tab-${td.key}`}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              tab === td.key ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {t(td.tkey, td.fallback)}
          </button>
        ))}
      </div>

      {tab === 'backorders' && <BackordersTab authHeaders={authHeaders} token={token} />}
      {tab === 'suppliers' && <SuppliersTab authHeaders={authHeaders} />}
      {tab === 'pos' && <PurchaseOrdersTab authHeaders={authHeaders} token={token} />}
    </div>
  );
}

// ===== Backorders tab =====
function BackordersTab({ authHeaders }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/backorders/aggregate`, { headers: authHeaders });
      const d = await r.json();
      setRows(d.backorders || []);
    } finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;
  if (rows.length === 0) return <Empty text={t('admin.supply_page.no_backorders', 'Žiadne nevybavené predobjednávky.')} />;

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produkt</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.supply_page.col_current_stock', 'Aktuálny stav')}</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dopyt</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.supply_page.col_customers', 'Zákazníci')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map(b => (
            <tr key={b.product_id} className="hover:bg-gray-50" data-testid={`backorder-${b.product_id}`}>
              <td className="px-4 py-4">
                <div className="font-semibold">{b.name}</div>
                <div className="text-xs text-gray-500 font-mono">{b.sku}</div>
              </td>
              <td className="px-4 py-4 text-sm">
                <div>stock: {b.current_stock.toFixed(0)} {b.unit}</div>
                <div className="text-gray-500">{t('admin.supply_page.reserved', 'rezervované')}: {b.current_reserved.toFixed(0)}</div>
              </td>
              <td className="px-4 py-4">
                <span className="font-bold text-lg text-orange-600">
                  {b.total_requested ? b.total_requested.toFixed(0) : '—'} {b.unit}
                </span>
                <div className="text-xs text-gray-500">{b.subscriber_count} podpisov</div>
              </td>
              <td className="px-4 py-4 text-xs">
                {b.subscribers.slice(0, 3).map((s, i) => (
                  <div key={i} className="text-gray-600">
                    {s.email}
                    {s.requested_quantity > 0 && <span className="ml-1 text-orange-600">({s.requested_quantity.toFixed(0)})</span>}
                  </div>
                ))}
                {b.subscribers.length > 3 && <div className="text-gray-400">+{b.subscribers.length - 3} {t('admin.supply_page.more', 'ďalších')}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===== Suppliers tab =====
function SuppliersTab({ authHeaders }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | supplier obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/suppliers`, { headers: authHeaders });
      const d = await r.json();
      setRows(d.suppliers || []);
    } finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    const isNew = !data.id;
    const url = isNew
      ? `${API_URL}/api/admin/suppliers`
      : `${API_URL}/api/admin/suppliers/${data.id}`;
    const r = await fetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: authHeaders,
      body: JSON.stringify(data),
    });
    if (!r.ok) { alert('Chyba: ' + (await r.text())); return; }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm(t('admin.supply_page.delete_supplier_confirm', 'Zmazať dodávateľa?'))) return;
    const r = await fetch(`${API_URL}/api/admin/suppliers/${id}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    const d = await r.json();
    if (!r.ok) { alert('Chyba: ' + (d.detail || 'unknown')); return; }
    load();
  };

  return (
    <div>
      <div className="mb-4">
        <Button onClick={() => setEditing({})} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-supplier">
          <Plus className="h-4 w-4 mr-1" /> {t('admin.supply_page.new_supplier', 'Nový dodávateľ')}
        </Button>
      </div>
      {loading ? <Loader /> : rows.length === 0 ? <Empty text={t('admin.supply_page.no_suppliers', 'Žiadni dodávatelia.')} /> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.supply_page.col_name', 'Názov')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kontakt</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Krajina</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 font-semibold">{s.name}</td>
                  <td className="px-4 py-4 text-sm">
                    <div>{s.email}</div>
                    <div className="text-gray-500">{s.phone} {s.contact_person && `· ${s.contact_person}`}</div>
                  </td>
                  <td className="px-4 py-4">{s.country}</td>
                  <td className="px-4 py-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => setEditing(s)}>{t('admin.supply_page.edit', 'Upraviť')}</Button>
                    <Button size="sm" variant="outline" onClick={() => remove(s.id)} className="ml-2"><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && <SupplierForm supplier={editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function SupplierForm({ supplier, onSave, onCancel }) {
  const { t } = useTranslation();
  const [data, setData] = useState({
    id: supplier.id,
    name: supplier.name || '',
    email: supplier.email || '',
    phone: supplier.phone || '',
    address: supplier.address || '',
    country: supplier.country || '',
    contact_person: supplier.contact_person || '',
    notes: supplier.notes || '',
  });
  const setField = (k) => (e) => setData(d => ({ ...d, [k]: e.target.value }));
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="text-lg font-bold">{supplier.id ? t('admin.supply_page.edit_supplier', 'Upraviť dodávateľa') : t('admin.supply_page.new_supplier', 'Nový dodávateľ')}</h3>
          <button onClick={onCancel}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {['name', 'email', 'phone', 'contact_person', 'address', 'country'].map(field => (
            <label key={field} className="block text-sm">
              <span className="text-gray-700 capitalize">{field.replace('_', ' ')}</span>
              <input
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                value={data[field]}
                onChange={setField(field)}
                data-testid={`supplier-${field}`}
              />
            </label>
          ))}
          <label className="block text-sm">
            <span className="text-gray-700">Notes</span>
            <textarea
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={2}
              value={data.notes}
              onChange={setField('notes')}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel}>{t('common.cancel', 'Zrušiť')}</Button>
            <Button onClick={() => onSave(data)} className="bg-orange-500 hover:bg-orange-600 text-white" disabled={!data.name.trim()}>
              {t('common.save', 'Uložiť')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Purchase Orders tab =====
function PurchaseOrdersTab({ authHeaders, token }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'sk').split('-')[0];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/purchase-orders`, { headers: authHeaders });
      const d = await r.json();
      setRows(d.purchase_orders || []);
    } finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (po, newStatus) => {
    const r = await fetch(`${API_URL}/api/admin/purchase-orders/${po.id}/status`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ status: newStatus }),
    });
    const d = await r.json();
    if (!r.ok) { alert('Chyba: ' + (d.detail || 'unknown')); return; }
    load();
  };

  const downloadPDF = async (po) => {
    const r = await fetch(`${API_URL}/api/admin/purchase-orders/${po.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) { alert('Chyba sťahovania'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${po.number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-4">
        <Button onClick={() => setCreating(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="new-po">
          <Plus className="h-4 w-4 mr-1" /> {t('admin.supply_page.new_po', 'Nová objednávka tovaru')}
        </Button>
      </div>

      {loading ? <Loader /> : rows.length === 0 ? <Empty text={t('admin.supply_page.no_pos', 'Žiadne objednávky tovaru.')} /> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.supply_page.col_no', 'Č.')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.supply_page.col_supplier', 'Dodávateľ')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.supply_page.col_items', 'Položky')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Suma</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.supply_page.col_date', 'Dátum')}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map(po => {
                const s = PO_STATUS[po.status] || { tkey: null, fallback: po.status, cls: 'bg-gray-100 text-gray-700' };
                const sLabel = s.tkey ? t(s.tkey, s.fallback) : s.fallback;
                return (
                  <tr key={po.id} className="hover:bg-gray-50" data-testid={`po-${po.number}`}>
                    <td className="px-4 py-4 font-mono text-sm">{po.number}</td>
                    <td className="px-4 py-4">{po.supplier_snapshot?.name}</td>
                    <td className="px-4 py-4 text-xs text-gray-600">
                      {po.items?.slice(0, 2).map((i, idx) => (
                        <div key={idx}>{i.sku} × {i.quantity}{i.unit}</div>
                      ))}
                      {po.items?.length > 2 && <div>+{po.items.length - 2}…</div>}
                    </td>
                    <td className="px-4 py-4 font-medium">{po.subtotal?.toFixed(2)} {po.currency}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${s.cls}`}>
                        {sLabel}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">{formatDate(po.created_at, lang)}</td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => downloadPDF(po)}>
                          <FileDown className="h-4 w-4" />
                        </Button>
                        {po.status === 'draft' && (
                          <Button size="sm" variant="outline" onClick={() => changeStatus(po, 'sent')} title={t('admin.supply_page.mark_sent', 'Označiť ako odoslané')}>
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        {po.status === 'sent' && (
                          <Button size="sm" variant="outline" onClick={() => changeStatus(po, 'received')} title={t('admin.supply_page.received_stock', 'Prijaté na sklad')}>
                            <PackageCheck className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <PurchaseOrderForm
          authHeaders={authHeaders}
          onCreated={() => { setCreating(false); load(); }}
          onCancel={() => setCreating(false)}
        />
      )}
    </div>
  );
}

function PurchaseOrderForm({ authHeaders, onCreated, onCancel }) {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ product_id: '', quantity: '', unit_price: '' }]);

  useEffect(() => {
    (async () => {
      const s = await (await fetch(`${API_URL}/api/admin/suppliers`, { headers: authHeaders })).json();
      setSuppliers(s.suppliers || []);
      const p = await (await fetch(`${API_URL}/api/products?limit=2000`)).json();
      setProducts(p);
    })();
  }, [authHeaders]);

  const addItem = () => setItems([...items, { product_id: '', quantity: '', unit_price: '' }]);
  const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const submit = async () => {
    const valid = items.filter(it => it.product_id && Number(it.quantity) > 0);
    if (!supplierId || valid.length === 0) { alert('Vyber dodávateľa a aspoň jednu položku'); return; }
    const r = await fetch(`${API_URL}/api/admin/purchase-orders`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        supplier_id: supplierId,
        notes,
        items: valid.map(it => ({
          product_id: it.product_id,
          quantity: Number(it.quantity),
          unit_price: Number(it.unit_price) || 0,
        })),
      }),
    });
    const d = await r.json();
    if (!r.ok) { alert('Chyba: ' + (d.detail || 'unknown')); return; }
    onCreated();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="text-lg font-bold">{t('admin.supply_page.new_po', 'Nová objednávka tovaru')}</h3>
          <button onClick={onCancel}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block text-sm">
            <span className="text-gray-700">{t('admin.supply_page.col_supplier', 'Dodávateľ')}</span>
            <select
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              data-testid="po-supplier"
            >
              <option value="">— vyber —</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{t('admin.supply_page.col_items', 'Položky')}</span>
              <Button size="sm" variant="outline" onClick={addItem}><Plus className="h-4 w-4 mr-1" /> {t('admin.supply_page.add', 'Pridať')}</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <select
                    className="flex-1 px-2 py-1 border rounded"
                    value={it.product_id}
                    onChange={(e) => setItem(i, 'product_id', e.target.value)}
                  >
                    <option value="">— produkt —</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.translations?.sk?.name}</option>)}
                  </select>
                  <input type="number" step="0.1" placeholder={t('admin.supply_page.qty_short', 'Množ.')} className="w-24 px-2 py-1 border rounded"
                         value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} />
                  <input type="number" step="0.01" placeholder="€/jedn." className="w-28 px-2 py-1 border rounded"
                         value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} />
                  <Button size="sm" variant="outline" onClick={() => rmItem(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>

          <label className="block text-sm">
            <span className="text-gray-700">{t('admin.supply_page.note', 'Poznámka')}</span>
            <textarea rows={2} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onCancel}>{t('common.cancel', 'Zrušiť')}</Button>
            <Button onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="po-submit">
              {t('admin.supply_page.create', 'Vytvoriť')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Shared helpers =====
function Loader() {
  return (
    <div className="bg-white rounded-xl shadow p-8 text-center">
      <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
    </div>
  );
}
function Empty({ text }) {
  return <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500" data-testid="empty-state">{text}</div>;
}
